import React, { useState, useRef, useEffect } from 'react';
import EventDetails from './EventDetails';
import { getLocalDateKey } from '../utils/eventUtils';

// Helper to get time slots from 7am to 9pm
const getTimeSlots = () => {
  const slots = [];
  for (let hour = 7; hour <= 21; hour++) {
    slots.push(hour);
  }
  return slots;
};

// Helper to format hour
const formatHour = (hour) => {
  const ampm = hour >= 12 ? 'pm' : 'am';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${ampm}`;
};

// Helper to get events for a day
const getEventsForDay = (events, day) => {
  const dayStr = getLocalDateKey(day);
  return events.filter(e => getLocalDateKey(e.startDate) === dayStr);
};

// Helper to get all-day events for a day
const getAllDayEventsForDay = (events, day) => {
  const dayStr = getLocalDateKey(day);
  return events.filter(e => {
    const start = new Date(e.startDate);
    return getLocalDateKey(e.startDate) === dayStr && start.getHours() === 0 && start.getMinutes() === 0;
  });
};

// Helper to find overlapping event groups for a day
function getEventColumns(events) {
  // Sort events by start time
  const sorted = [...events].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  // For each event, assign the first available column that doesn't overlap
  const columns = [];
  const eventMeta = {};

  for (const event of sorted) {
    const start = new Date(event.startDate);
    const end = new Date(event.endDate || event.startDate);

    // Find the first column where this event doesn't overlap with the last event in that column
    let colIdx = 0;
    while (true) {
      if (!columns[colIdx]) {
        columns[colIdx] = [];
        break;
      }
      // Check if this event overlaps with the last event in this column
      const lastEventInCol = columns[colIdx][columns[colIdx].length - 1];
      const lastEnd = new Date(lastEventInCol.endDate || lastEventInCol.startDate);
      if (start >= lastEnd) {
        break;
      }
      colIdx++;
    }
    columns[colIdx].push(event);
    eventMeta[event.id] = { colIdx, total: null }; // We'll set total later
  }

  // For each event, set the total number of columns in its overlap group
  // We'll do this by, for each event, finding all events it overlaps with and getting the max column index among them
  for (const event of sorted) {
    const start = new Date(event.startDate);
    const end = new Date(event.endDate || event.startDate);
    let maxCol = 0;
    for (const other of sorted) {
      if (event === other) continue;
      const oStart = new Date(other.startDate);
      const oEnd = new Date(other.endDate || other.startDate);
      // If they overlap
      if (!(end <= oStart || start >= oEnd)) {
        maxCol = Math.max(maxCol, eventMeta[other.id].colIdx);
      }
    }
    eventMeta[event.id].total = Math.max(maxCol, eventMeta[event.id].colIdx) + 1;
  }

  return eventMeta;
}

const WeekView2 = ({ days, events, theme = {}, onDaySelect, onEventSelect }) => {
  const [hoveredEventId, setHoveredEventId] = useState(null);
  const timeSlots = getTimeSlots();
  const hourHeight = 48; // px per hour
  const totalHours = 21 - 7 + 1; // 15 hours
  const gridHeight = hourHeight * totalHours;
  
  if (!theme) {
    theme = {
      eventBg: 'bg-blue-100',
      eventHeaderBg: 'bg-blue-200',
      modalBg: 'bg-white',
      modalOverlay: 'bg-black bg-opacity-50',
      text: 'text-gray-900',
      secondaryText: 'text-gray-600',
      buttonHover: 'hover:text-gray-800',
      todayBarBackground: 'bg-blue-100',
      todayBackground: 'bg-blue-500 text-white'
    };
  }

  // Handle day selection
  const handleDayClick = (day) => {
    if (onDaySelect) {
      onDaySelect(day);
    }
  };

  // Handle event selection
  const handleEventClick = (event, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Set the selected day to the event's start date
    const eventDate = new Date(event.startDate);
    if (onDaySelect) {
      onDaySelect(eventDate);
    }
    if (onEventSelect) {
      onEventSelect(event, e);
    }
  };

  return (
    <div className={`w-full max-w-full h-full overflow-x-auto overflow-y-auto ${theme.text}`}>
      <div className={`flex border ${theme.border} rounded-lg shadow-lg`}>
        {/* Time column (sticky on small screens) */}
        <div className={`flex flex-col left-0 z-20 ${theme.headerBg} w-[60px] min-w-[60px] sticky top-0`}>
          <div className={`${theme.headerBg} border-b ${theme.border} border-r p-2 font-bold text-xs text-center sticky left-0 top-0 z-30 h-[32px] w-[60px] min-w-[60px] max-w-[60px]`}>
            Time
          </div>
          {/* All Day row label (sticky below header) */}
          <div className={`${theme.headerBg} border-b ${theme.border} border-r p-1 text-xs text-right font-bold sticky left-0 z-20 h-[96px] w-[60px] min-w-[60px] max-w-[60px] flex items-center justify-end`}>All Day</div>
          {timeSlots.map((hour, idx) => (
            <div key={hour} className={`border-b ${theme.border} border-r p-1 text-xs text-right ${theme.headerBg} ${theme.secondaryText} sticky left-0 z-10 h-[48px] w-[60px] min-w-[60px] max-w-[60px] flex items-center justify-end`}>
              {formatHour(hour)}
            </div>
          ))}
        </div>
        {/* Day columns */}
        {days.map((day, dayIdx) => {
          const allTimedEvents = getEventsForDay(events, day).filter(e => {
            const start = new Date(e.startDate);
            return !(start.getHours() === 0 && start.getMinutes() === 0);
          });
          // Events outside the visible 7am–9pm grid go to the all-day row with a time badge
          const outOfRangeEvents = allTimedEvents.filter(e => {
            const startHour = new Date(e.startDate).getHours() + new Date(e.startDate).getMinutes() / 60;
            const endHour = new Date(e.endDate || e.startDate).getHours() + new Date(e.endDate || e.startDate).getMinutes() / 60;
            return endHour <= 7 || startHour >= 21;
          });
          // Only timed events within the visible range go in the hour grid
          const dayEvents = allTimedEvents.filter(e => {
            const startHour = new Date(e.startDate).getHours() + new Date(e.startDate).getMinutes() / 60;
            const endHour = new Date(e.endDate || e.startDate).getHours() + new Date(e.endDate || e.startDate).getMinutes() / 60;
            return !(endHour <= 7 || startHour >= 21);
          });
          const allDayEvents = getAllDayEventsForDay(events, day);
          const eventColumns = getEventColumns(dayEvents);
          const today = new Date();
          const isToday = day.toDateString() === today.toDateString();

          return (
            <div key={dayIdx} className="flex flex-col flex-1 min-w-[120px] border-l">
              {/* Day header */}
              <div 
                className={`${isToday ? theme.todayBarBackground : theme.dayHeaderBg} border-b ${theme.border} p-2 font-bold text-xs text-center sticky top-0 z-30 h-[32px] flex items-center justify-center ${
                  window.innerWidth < 768 && getEventsForDay(events, day).length === 0
                    ? 'cursor-not-allowed opacity-50'
                    : `cursor-pointer ${theme.pickerHover}`
                }`}
                onClick={() => handleDayClick(day)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleDayClick(day);
                  }
                }}
                aria-label={`View events for ${day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`}
              >
                <span className={`block pl-2 pr-2 rounded-full ${isToday ? theme.todayBackground : ''} flex items-center justify-center`}>
                  {day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
              </div>
              {/* All Day row — includes true all-day events and out-of-range timed events */}
              <div className={`relative overflow-y-auto border-b ${theme.border} flex flex-wrap items-start gap-1 p-1 ${theme.gridBg} z-20 h-[96px]`}>
                {allDayEvents.map(event => (
                  <div
                    key={event.id}
                    className={`hover:opacity-90 border-2 ${theme.eventBg} ${theme.eventBorder || theme.border} overflow-hidden mb-1 shadow-lg cursor-pointer px-2 py-1 rounded w-full`}
                    title={event.title}
                    onClick={(e) => handleEventClick(event, e)}
                  >
                    {event.eventType === 'Featured' && (
                      <span className="bg-yellow-400 text-black text-xs px-1 py-0.5 rounded-full ml-1 mr-1" aria-label="Featured event">
                        <i className="fas fa-star" aria-hidden="true"></i>
                      </span>
                    )}
                    <div className="font-bold text-xs truncate">{event.title}</div>
                    <div className={`text-xs truncate opacity-75 ${theme.eventHeaderBg}`}>{event.organization}</div>
                    {event.sponsorLogo && (
                      <div className="mt-2 text-center">
                        <span className={`text-xs ${theme.secondaryText}`}>Hosted By</span>
                        <img src={event.sponsorLogo} alt={event.sponsorName || 'Event Host'} className="max-h-8 mx-auto" />
                      </div>
                    )}
                  </div>
                ))}
                {outOfRangeEvents.map(event => (
                  <div
                    key={event.id}
                    className={`hover:opacity-90 border-2 border-dashed ${theme.eventBorder || theme.border} ${theme.eventBg} overflow-hidden mb-1 shadow-lg cursor-pointer px-2 py-1 rounded w-full`}
                    title={`${event.title} — outside visible hours`}
                    onClick={(e) => handleEventClick(event, e)}
                  >
                    <div className={`text-[10px] ${theme.secondaryText} flex items-center gap-1 mb-0.5`}>
                      <i className="fas fa-clock" aria-hidden="true"></i>
                      {new Date(event.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    {event.eventType === 'Featured' && (
                      <span className="bg-yellow-400 text-black text-xs px-1 py-0.5 rounded-full ml-1 mr-1" aria-label="Featured event">
                        <i className="fas fa-star" aria-hidden="true"></i>
                      </span>
                    )}
                    <div className="font-bold text-xs truncate">{event.title}</div>
                    <div className={`text-xs truncate opacity-75 ${theme.eventHeaderBg}`}>{event.organization}</div>
                  </div>
                ))}
              </div>
              {/* Grid for hours */}
              <div className={`relative flex-1 ${theme.background} opacity-90`} style={{ height: `${gridHeight}px` }}>
                {/* Hour grid lines */}
                {timeSlots.map((hour, idx) => (
                  <div
                    key={hour}
                    className={`absolute left-0 right-0 border-b ${theme.border}`}
                    style={{ top: `${idx * hourHeight}px`, height: `${hourHeight}px` }}
                  />
                ))}
                {/* Event blocks */}
                {dayEvents.map(event => {
                  const start = new Date(event.startDate);
                  const end = new Date(event.endDate || event.startDate);
                  const startHour = start.getHours() + start.getMinutes()/60;
                  const endHour = end.getHours() + end.getMinutes()/60;
                  // Clamp to visible range
                  const clampedStart = Math.max(startHour, 7);
                  const clampedEnd = Math.min(endHour, 21);
                  const top = (clampedStart - 7) * hourHeight;
                  let height = (clampedEnd - clampedStart) * hourHeight;
                  // If event is exactly one hour, set height to hourHeight
                  if (Math.abs(clampedEnd - clampedStart) <= 1.01) height = hourHeight;
                  const meta = eventColumns[event.id];
                  const isHovered = hoveredEventId === event.id;
                  const width = isHovered ? 100 : 100 / meta.total;
                  const left = isHovered ? 0 : meta.colIdx * (100 / meta.total);
                  const zIndex = isHovered ? 10 : 2;
                  return (
                    <div
                      key={event.id}
                      style={{
                        top,
                        height,
                        left: `${left}%`,
                        width: `${width}%`,
                        position: 'absolute',
                        borderRadius: '0.25rem',
                        padding: '2px 4px',
                        zIndex,
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                      }}
                      title={event.organization + ' - ' + event.title}
                      className={`hover:opacity-90 border-2 text-xs overflow-hidden ${theme.eventBg} ${theme.eventBorder || theme.border} shadow-lg`}
                      onClick={(e) => handleEventClick(event, e)}
                      onMouseEnter={() => setHoveredEventId(event.id)}
                      onMouseLeave={() => setHoveredEventId(null)}
                    >
                      {event.eventType === 'Featured' && (
                        <span className="bg-yellow-400 text-black text-xs px-1 py-0.5 rounded-full ml-1 mr-1" aria-label="Featured event">
                          <i className="fas fa-star" aria-hidden="true"></i>
                        </span>
                      )}
                      <div className="font-bold text-xs truncate">{event.title}</div>
                      <div className={`text-xs truncate opacity-75 ${theme.eventHeaderBg}`}>{event.organization}</div>
                      <div className="text-[10px]">{new Date(event.startDate).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})} - {new Date(event.endDate || event.startDate).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</div>
                      {event.sponsorLogo && (
                        <div className="mt-2 text-center">
                          <span className={`text-xs ${theme.secondaryText}`}>Hosted By</span>
                          <img src={event.sponsorLogo} alt={event.sponsorName || 'Event Host'} className="max-h-8 mx-auto" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeekView2; 